import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { Formik, Field, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';
import { Container, Row, Col, Card, Button } from "react-bootstrap";

const IndustryEdit: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [industry, setIndustry] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [redirectAfterModal, setRedirectAfterModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [pendingValues, setPendingValues] = useState<any>(null); // temporarily store form values

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}industries/get-industry/${id}`
        );
        if (response.data) {
          setIndustry(response.data);
        } else {
          setModalMessage("Industry not found.");
          setShowModal(true);
        }
      } catch (error) {
        console.log(error);
        setFailureMessage("Error loading data. Please try again later.");
        setShowFailureModal(true);
      }
    };

    fetchIndustry();
  }, [id]);

  const callApiUpdateIndustry = async (values: any) => {
    const url = `${process.env.REACT_APP_API_URL}industries/update-industry/${id}`;
    try {
      const response = await axios.put(url, {
        name: values.name,
        industry_icon: values.industry_icon || null,
      });

      if (response.status === 200) {
        setSuccessMessage("Industry updated successfully!");
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/industries");
        }, 3000);
      }
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to update industry. Please try again.");
      setShowFailureModal(true);
    }
  };

  const handleSubmit = async (values: any) => {
    const hasChanges =
      values.name.trim() !== industry.name.trim() ||
      values.industry_icon.trim() !== industry.industry_icon.trim();

    if (!hasChanges) {
      setModalMessage("No changes were made.");
      setShowModal(true);
      return;
    }

    setModalMessage("Are you sure you want to update this industry?");
    setPendingValues(values); // store values to use after confirmation
    setRedirectAfterModal(true);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (redirectAfterModal && pendingValues) {
      callApiUpdateIndustry(pendingValues);
      setPendingValues(null);
      setRedirectAfterModal(false);
    }
  };

  if (!industry) {
    return <div className="text-center mt-5">Loading industry details...</div>;
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center py-2">
              <h4>Edit Industry</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{ name: industry.name || "", industry_icon: industry.industry_icon || "" }}
                validate={(values) => {
                  const errors: any = {};
                  if (!values.name.trim()) {
                    errors.name = "Industry name is required";
                  }
                  if (values.industry_icon && !/^fas fa-[a-z-]+$/.test(values.industry_icon)) {
                    errors.industry_icon = "Invalid Font Awesome class format";
                  }
                  return errors;
                }}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ errors, touched, values }) => (
                  <Form>
                    <Row className="mb-3">
                      <Col>
                        <label htmlFor="name" className="form-label">
                          Industry Name:
                        </label>
                        <Field
                          name="name"
                          type="text"
                          className={`form-control ${
                            errors.name && touched.name ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Name"
                        />
                        {errors.name && touched.name && (
                          <div className="invalid-feedback">{String(errors.name)}</div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col>
                        <label htmlFor="industry_icon" className="form-label">
                          Industry Icon (Optional):
                        </label>
                        <Field
                          name="industry_icon"
                          type="text"
                          className="form-control"
                          placeholder="Enter Font Awesome class (e.g., fas fa-industry)"
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button
                          type="submit"
                          variant="success"
                          disabled={
                            !!errors.name ||
                            (values.name.trim() === industry.name.trim() &&
                              !values.industry_icon)
                          }
                        >
                          Update
                        </Button>
                        <Button
                          variant="danger"
                          className="mx-2"
                          onClick={() => navigate("/industries")}
                        >
                          Go Back
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showModal}
        onHide={handleModalClose}
        modalMessage={modalMessage}
        operationType="Confirm"
      />

      <NewconfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        modalMessage={successMessage}
        operationType="Ok"
        timeoutDuration={3000}
      />

      <AllFailureModal
        show={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        modalMessage={failureMessage}
        operationType="Ok"
        timeoutDuration={5000}
      />
    </Container>
  );
};

export default IndustryEdit;
